import { useState, useEffect, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';

// GTM (a) 2026-09-12: users arrive server-sorted by org then facility; interleave
// group header rows whenever the org/facility label changes. Grayscale sees all
// tenants in the current tenant view; owners/org_admins see only their own.
function withGroupHeaders(list) {
  const out = []; let last = null;
  const act = (list || []).filter(u => u.active);
  const inact = (list || []).filter(u => !u.active);
  act.forEach(u => {
    const org = u.org_name || 'Unassigned';
    const fac = (u.facility_name && u.facility_name !== u.org_name) ? u.facility_name : '';
    const label = fac ? org + ' \u2014 ' + fac : org;
    if (label !== last) { out.push({ __header: true, __key: 'hdr_' + out.length, __label: label }); last = label; }
    out.push(u);
  });
  if (inact.length) {
    out.push({ __header: true, __key: 'hdr_inactive', __label: 'Inactive (' + inact.length + ')' });
    inact.forEach(u => out.push(u));
  }
  return out;
}

// Boot fallback only - the picker goes live from role_definitions via /api/roles (2026-08-28)
const ROLES_FALLBACK = [
  { key: 'ceo',            label: 'CEO',             desc: 'Everything' },
  { key: 'admin',          label: 'Admin',           desc: 'Everything' },
  { key: 'ur_supervisor',  label: 'UR Supervisor',   desc: 'UR, Bed Board, Alumni' },
  { key: 'ur_coordinator', label: 'UR Coordinator',  desc: 'UR only' },
  { key: 'admissions',     label: 'Admissions',      desc: 'Queue, Calls, Leads, Bed Board' },
  { key: 'clinical',       label: 'Clinical (legacy)', desc: 'EMR, Bed Board - migrate to a role below' },
  { key: 'clinical_director', label: 'Clinical Director', desc: 'Full clinical authority, cosigns, manages clinical users' },
  { key: 'therapist',      label: 'Therapist',       desc: 'Chart, notes, TP, groups, UR concurrent, discharge' },
  { key: 'case_manager',   label: 'Case Manager',    desc: 'Therapist set + med inventory (except HBTS)' },
  { key: 'program_manager', label: 'Program Manager', desc: 'Case manager set + operations/BHT oversight, scheduler' },
  { key: 'medical',        label: 'Medical Team',    desc: 'Orders, MARs, labs w/ sign-off, nursing notes' },
  { key: 'medical_admin',  label: 'Medical Admin',   desc: 'Medical group admin staff - manages Medical Team accounts only' },
  { key: 'billing',        label: 'Billing',         desc: 'Collections, Reports' },
  { key: 'zealie',         label: 'Biller (external)', desc: 'Ready to Bill only' },
  { key: 'operations',     label: 'Operations',      desc: 'Bed Board, Rounds, Tickets' },
  { key: 'bht',            label: 'BHT',             desc: 'Rounds, Bed Board, Scheduler' },
];

const ROLE_COLOR = {
  ceo:            { bg: '#336090', fg: 'white' },
  admin:          { bg: '#1e40af', fg: 'white' },
  ur_supervisor:  { bg: '#7c3aed', fg: 'white' },
  ur_coordinator: { bg: '#a78bfa', fg: 'white' },
  operations:     { bg: '#0e7490', fg: 'white' },
  bht:            { bg: '#67e8f9', fg: '#164e63' },
  admissions:     { bg: '#059669', fg: 'white' },
  clinical:       { bg: '#0891b2', fg: 'white' },
  clinical_director: { bg: '#166534', fg: 'white' },
  therapist:      { bg: '#16a34a', fg: 'white' },
  case_manager:   { bg: '#4ade80', fg: '#14532d' },
  program_manager: { bg: '#0d9488', fg: 'white' },
  medical:        { bg: '#dc2626', fg: 'white' },
  medical_admin:  { bg: '#f87171', fg: 'white' },
  billing:        { bg: '#b45309', fg: 'white' },
  zealie:         { bg: '#0e7490', fg: 'white' },
};

const s = {
  container: { padding: '32px', maxWidth: '1000px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' },
  title: { margin: '0 0 4px', fontSize: '24px', color: '#336090' },
  subtitle: { margin: 0, color: '#666', fontSize: '14px' },
  addBtn: { background: '#336090', color: 'white', border: 'none', padding: '9px 18px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },

  banner: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  bannerX: { background: 'none', border: 'none', cursor: 'pointer', color: '#166534', fontSize: '16px' },

  table: { width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  th: { padding: '11px 14px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em', background: '#f8fafc', borderBottom: '2px solid #e5e7eb' },
  td: { padding: '12px 14px', borderBottom: '1px solid #f0f4f8', fontSize: '14px', color: '#1a2a3a', verticalAlign: 'middle' },

  roleTag: { display: 'inline-block', padding: '3px 9px', borderRadius: '12px', fontSize: '10px', fontWeight: '800', letterSpacing: '0.04em' },
  pill: { display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '10px', fontWeight: '700' },

  btnSm: { padding: '5px 11px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #d1d5db', background: 'white', color: '#374151', marginRight: '6px' },
  btnDanger: { padding: '5px 11px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #fca5a5', background: '#fef2f2', color: '#991b1b' },
  btnOn: { padding: '5px 11px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534' },

  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' },
  modalBox: { background: 'white', borderRadius: '16px', width: '100%', maxWidth: '480px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  modalHeader: { padding: '20px 24px', background: '#336090', position: 'relative' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: 'white' },
  modalSub: { fontSize: '13px', color: 'rgba(255,255,255,0.75)', marginTop: '3px' },
  modalClose: { position: 'absolute', top: '16px', right: '16px', background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: '28px', height: '28px', borderRadius: '50%', cursor: 'pointer' },
  modalBody: { padding: '20px 24px' },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #f0f4f8', display: 'flex', justifyContent: 'flex-end', gap: '10px' },
  saveBtn: { padding: '9px 20px', background: '#336090', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  cancelBtn: { padding: '9px 20px', background: 'white', color: '#374151', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },

  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  field: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '12px' },
  label: { fontSize: '11px', fontWeight: '600', color: '#666', textTransform: 'uppercase', letterSpacing: '0.06em' },
  input: { padding: '9px 11px', border: '1px solid #d1d5db', borderRadius: '7px', fontSize: '14px', width: '100%', boxSizing: 'border-box' },
  err: { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', marginBottom: '12px' },
  hint: { fontSize: '12px', color: '#666', marginTop: '6px', lineHeight: 1.5 },
  empty: { textAlign: 'center', padding: '50px', color: '#999' },
};

const TEMP_PW = 'Welcome2026!';

// Roles whose users write clinical notes (cosign fields shown) / can serve as cosigners
const CLINICAL_FAMILY = ['clinical_director','therapist','case_manager','program_manager','medical','bht','clinical'];

function UserModal({ user, token, onClose, onSaved, allUsers }) {
  const editing = !!user;
  const [form, setForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    role: user?.role || 'admissions',
    requires_cosign: user?.requires_cosign || false,
    cosigner_user_id: user?.cosigner_user_id || null,
    facility_id: user?.facility_id || null,
    facility_access: Array.isArray(user?.facility_access) ? user.facility_access : [],
    credentials: user?.credentials || '',
    license_number: user?.license_number || '',
    license_state: user?.license_state || '',
  });
  const [orgs, setOrgs] = useState([]);
  const [ROLES, setROLES] = useState(ROLES_FALLBACK);
  useEffect(() => {
    fetch(`${API}/api/roles`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        const arr = Array.isArray(j) ? j : (j.data || j.roles || []);
        const live = arr
          .filter(r => r.active !== false)
          .map(r => ({ key: r.role_key || r.key, label: r.display_name || r.label || r.role_key, desc: r.description || r.desc || '' }))
          .filter(r => r.key);
        if (live.length) setROLES(live);
      })
      .catch(() => {});
    fetch(`${API}/api/organizations`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => setOrgs((j.data || []).filter(o =>
        o.active && !o.hidden && ['facility', 'management_group'].includes(o.org_type))))
      .catch(() => {});
  }, [token]);
  const boundOrg = orgs.find(o => o.id === form.facility_id);
  const isGroupBound = boundOrg && boundOrg.org_type === 'management_group';
  const groupFacilities = isGroupBound
    ? orgs.filter(o => o.org_type === 'facility' && o.parent_org_id === form.facility_id)
    : [];
  const [perms, setPerms] = useState(user?.list_permissions || {});
  const setPerm = (list, level) => {
    setPerms(p => {
      const np = { ...p };
      if (np[list] === level) delete np[list]; else np[list] = level;
      return np;
    });
  };
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // suggest the house email pattern: firstinitiallastname@
  const suggested = (!editing && form.first_name && form.last_name)
    ? `${form.first_name[0]}${form.last_name}`.toLowerCase().replace(/[^a-z]/g, '') + '@infinitigroupllc.com'
    : '';

  const save = async () => {
    if (!form.first_name.trim() || !form.email.trim()) {
      setErr('First name and email are required.'); return;
    }
    setSaving(true); setErr('');
    try {
      const url = editing ? `${API}/api/users/${user.id}` : `${API}/api/users`;
      const res = await fetch(url, {
        method: editing ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        // 10/15: a facility-bound person who also works elsewhere keeps their home facility in the list too
        body: JSON.stringify({ ...form, facility_access: (!isGroupBound && form.facility_id && form.facility_access.length && !form.facility_access.includes(form.facility_id)) ? [form.facility_id, ...form.facility_access] : form.facility_access, list_permissions: perms }),
      });
      const j = await res.json();
      if (!j.ok) { setErr(j.error || 'Failed to save'); setSaving(false); return; }
      onSaved(editing ? null : `${form.first_name} created. Temp password: ${TEMP_PW}`);
    } catch (e) { setErr('Connection error'); }
    setSaving(false);
  };

  return (
    <div style={s.modalOverlay}>
      <div style={s.modalBox}>
        <div style={s.modalHeader}>
          <div style={s.modalTitle}>{editing ? 'Edit User' : 'Add User'}</div>
          <div style={s.modalSub}>{editing ? user.email : 'They will be forced to set a password on first login'}</div>
          <button onClick={onClose} style={s.modalClose}>✕</button>
        </div>
        <div style={s.modalBody}>
          {err && <div style={s.err}>{err}</div>}
          <div style={s.grid2}>
            <div style={s.field}>
              <label style={s.label}>First Name</label>
              <input style={s.input} value={form.first_name} autoFocus
                onChange={e => setForm({ ...form, first_name: e.target.value })} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Last Name</label>
              <input style={s.input} value={form.last_name}
                onChange={e => setForm({ ...form, last_name: e.target.value })} />
            </div>
          </div>

          {(
            <div style={s.field}>
              <label style={s.label}>Email</label>
              <input style={s.input} value={form.email} placeholder={suggested || 'name@infinitigroupllc.com'}
                onChange={e => setForm({ ...form, email: e.target.value })} />
              {suggested && form.email !== suggested && (
                <div style={s.hint}>
                  House pattern: <strong>{suggested}</strong>{' '}
                  <button onClick={() => setForm({ ...form, email: suggested })}
                    style={{ ...s.btnSm, padding: '2px 8px', marginLeft: 4 }}>use it</button>
                </div>
              )}
            </div>
          )}

          <div style={s.field}>
            <label style={s.label}>Role</label>
            <select style={s.input} value={form.role}
              onChange={e => setForm({ ...form, role: e.target.value })}>
              {ROLES.map(r => <option key={r.key} value={r.key}>{r.label} — {r.desc}</option>)}
            </select>
            <div style={s.hint}>
              Role controls which tabs they see. UR Coordinator sees only the UR tab.
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>CREDENTIALS &amp; LICENSE</label>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 1fr', gap:8 }}>
              <input style={s.input} placeholder="Credentials (MD, SUDRC, RADT...)"
                value={form.credentials}
                onChange={e => setForm({ ...form, credentials: e.target.value })} />
              <input style={s.input} placeholder="License #"
                value={form.license_number}
                onChange={e => setForm({ ...form, license_number: e.target.value })} />
              <input style={s.input} placeholder="State"
                value={form.license_state}
                onChange={e => setForm({ ...form, license_state: e.target.value })} />
            </div>
            <div style={s.hint}>
              Credentials print on signature blocks and drive the group-facilitator gate. No counseling credential = cannot be assigned to run groups.
            </div>
          </div>

          <div style={s.field}>
            <label style={s.label}>Facility</label>
            <select style={s.input} value={form.facility_id || ''}
              onChange={e => setForm({ ...form, facility_id: e.target.value || null, facility_access: [] })}>
              <option value="">{'\u2014'} Not assigned {'\u2014'}</option>
              {orgs.filter(o => o.org_type === 'management_group').map(o => (
                <option key={o.id} value={o.id}>{o.org_name} (all facilities)</option>))}
              {orgs.filter(o => o.org_type === 'facility').map(o => (
                <option key={o.id} value={o.id}>{o.display_name || o.org_name}</option>))}
            </select>
            <div style={s.hint}>
              A facility scopes who they can see and message. A management group sees all its facilities.
            </div>
            {(isGroupBound ? groupFacilities.length > 0 : !!form.facility_id) && (
              <div style={{ marginTop: 8 }}>
                <label style={s.label}>{isGroupBound ? 'Limit to specific facilities (optional)' : 'Also works at (other facilities)'}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
                  {(isGroupBound ? groupFacilities : orgs.filter(o => o.org_type === 'facility' && o.id !== form.facility_id)).map(o => {
                    const on = form.facility_access.includes(o.id);
                    return (
                      <button key={o.id} type="button"
                        onClick={() => setForm({ ...form, facility_access: on
                          ? form.facility_access.filter(x => x !== o.id)
                          : [...form.facility_access, o.id] })}
                        style={{ ...s.btnSm, padding: '4px 11px',
                          background: on ? '#1d4ed8' : '#fff', color: on ? '#fff' : '#334155',
                          border: '1px solid ' + (on ? '#1d4ed8' : '#cbd5e1') }}>
                        {o.display_name || o.org_name}</button>);
                  })}
                </div>
                <div style={s.hint}>
                  {isGroupBound ? 'Leave all unselected for full group visibility. Selecting any limits them to that subset.' : 'Pick every other facility this person works at. They will show in that facility\'s scheduler and see its clients.'}
                </div>
              </div>
            )}
          </div>

          {CLINICAL_FAMILY.includes(form.role) && (
            <div style={s.field}>
              <label style={s.label}>Cosign</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13 }}>
                <input type="checkbox" id="reqCosign" checked={!!form.requires_cosign}
                  onChange={e => setForm({ ...form, requires_cosign: e.target.checked })} />
                <label htmlFor="reqCosign" style={{ cursor: 'pointer' }}>
                  Requires cosign (everything they sign routes to their cosigner)
                </label>
              </div>
              <select style={{ ...s.input, marginTop: 6 }} value={form.cosigner_user_id || ''}
                onChange={e => setForm({ ...form, cosigner_user_id: e.target.value || null })}>
                <option value="">Cosigner: none</option>
                {(allUsers || [])
                  .filter(x => x.active && CLINICAL_FAMILY.includes(x.role) && x.id !== user?.id)
                  .map(x => (
                    <option key={x.id} value={x.id}>
                      {x.first_name} {x.last_name} ({(x.role || '').replace('_', ' ')})
                    </option>
                  ))}
              </select>
              <div style={s.hint}>
                Notes needing cosign route to this person's queue. Certain forms force cosign regardless of this flag. Takes effect on their next login.
              </div>
            </div>
          )}

          <div style={s.field}>
            <label style={s.label}>List Permissions (Staff & Lists page)</label>
            {[
              { key: 'staff',             label: 'Clinical Staff (therapists / case managers)' },
              { key: 'referral_partners', label: 'Referral Partners' },
              { key: 'lead_sources',      label: 'Lead Sources' },
            ].map(l => (
              <div key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '5px 0', fontSize: 13 }}>
                <span style={{ flex: 1 }}>{l.label}</span>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={perms[l.key] === 'add'} onChange={() => setPerm(l.key, 'add')} /> Add only
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={perms[l.key] === 'edit'} onChange={() => setPerm(l.key, 'edit')} /> Full edit
                </label>
              </div>
            ))}
            <div style={s.hint}>
              Grants show the Staff & Lists page with only the granted lists. CEO/Admin always have everything. Takes effect on their next login.
            </div>
          </div>
        </div>
        <div style={s.modalFooter}>
          <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={s.saveBtn}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create User'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Users({ user }) {
  const token = user?.token || localStorage.getItem('tcio_token');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [modal, setModal] = useState(null);

  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/users`, { headers: { Authorization: `Bearer ${token}` } });
      const j = await r.json();
      if (j.ok) setUsers(j.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset ${u.first_name}'s password to ${TEMP_PW}? They'll be forced to change it on next login.`)) return;
    try {
      const r = await fetch(`${API}/api/users/${u.id}/reset-password`, { method: 'POST', headers: H });
      const j = await r.json();
      if (j.ok) { setMsg(`${u.first_name}'s password is now ${TEMP_PW}`); load(); }
      else alert(j.error || 'Failed');
    } catch (e) { alert('Connection error'); }
  };

  const toggleActive = async (u) => {
    const turningOff = u.active;
    if (turningOff && !window.confirm(`Deactivate ${u.first_name} ${u.last_name}? They will not be able to log in.`)) return;
    try {
      const r = await fetch(`${API}/api/users/${u.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...H },
        body: JSON.stringify({ active: !u.active }),
      });
      const j = await r.json();
      if (j.ok) { setMsg(`${u.first_name} ${turningOff ? 'deactivated' : 'reactivated'}`); load(); }
      else alert(j.error || 'Failed');
    } catch (e) { alert('Connection error'); }
  };

  const fmtLogin = (d) => {
    if (!d) return <span style={{ color: '#ef4444', fontWeight: 600 }}>never</span>;
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const active = users.filter(u => u.active).length;
  const neverLoggedIn = users.filter(u => !u.last_login).length;

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h2 style={s.title}>Users</h2>
          <p style={s.subtitle}>
            {users.length} accounts · {active} active
            {neverLoggedIn > 0 && ` · ${neverLoggedIn} never logged in`}
          </p>
        </div>
        <button onClick={() => setModal({ type: 'add' })} style={s.addBtn}>+ Add User</button>
      </div>

      {msg && (
        <div style={s.banner}>
          <span>{msg}</span>
          <button onClick={() => setMsg('')} style={s.bannerX}>✕</button>
        </div>
      )}

      {loading ? (
        <div style={s.empty}>Loading…</div>
      ) : (
        <table style={s.table}>
          <thead>
            <tr>
              {['Name', 'Email', 'Role', 'Last Login', 'Status', ''].map((h, i) =>
                <th key={i} style={s.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {withGroupHeaders(users).map(u => {
              if (u.__header) return (
                <tr key={u.__key}>
                  <td colSpan={6} style={{ padding: '9px 14px', background: '#eef3f8',
                    fontWeight: 800, fontSize: '11px', letterSpacing: '0.05em',
                    color: '#336090', textTransform: 'uppercase',
                    borderTop: '2px solid #d8e3ee' }}>{u.__label}</td>
                </tr>);
              const rc = ROLE_COLOR[u.role] || { bg: '#9ca3af', fg: 'white' };
              return (
                <tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                  <td style={{ ...s.td, fontWeight: 600 }}>{u.first_name} {u.last_name}</td>
                  <td style={{ ...s.td, color: '#666', fontSize: '13px' }}>{u.email}</td>
                  <td style={s.td}>
                    <span style={{ ...s.roleTag, background: rc.bg, color: rc.fg }}>
                      {(u.role || '').replace('_', ' ').toUpperCase()}
                    </span>
                  </td>
                  <td style={{ ...s.td, fontSize: '13px' }}>
                    {fmtLogin(u.last_login)}
                    {u.must_change_password && (
                      <div style={{ ...s.pill, background: '#fef3c7', color: '#92400e', marginTop: 3 }}>
                        MUST CHANGE PW
                      </div>
                    )}
                  </td>
                  <td style={s.td}>
                    <span style={{
                      ...s.pill,
                      background: u.active ? '#dcfce7' : '#f3f4f6',
                      color: u.active ? '#166534' : '#6b7280',
                    }}>{u.active ? 'ACTIVE' : 'INACTIVE'}</span>
                  </td>
                  <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => setModal({ type: 'edit', user: u })} style={s.btnSm}>Edit</button>
                    <button onClick={() => resetPassword(u)} style={s.btnSm}>Reset PW</button>
                    <button onClick={() => toggleActive(u)}
                      style={u.active ? s.btnDanger : s.btnOn}>
                      {u.active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {modal && (
        <UserModal
          user={modal.type === 'edit' ? modal.user : null}
          allUsers={users}
          token={token}
          onClose={() => setModal(null)}
          onSaved={(m) => { setModal(null); if (m) setMsg(m); load(); }}
        />
      )}
    </div>
  );
}
